import { type CaptureFileJSONObject, IkeUnitAngle, IkeUnitDistance } from '@ikegps/core-api';
import type { Point, Size } from '@ikegps/core-api';
import { get } from 'lodash';
import {
	CollectionModelDrawable,
	CollectionModelDrawableLabel,
	CollectionModelDrawableLine,
	CollectionModelDrawableMarker,
	CollectionModelDrawableMarkerOffset,
	CollectionModelDrawableMask,
	CollectionModelDrawableVerticalSeparation,
} from '../../../model';
import { IkeUnit } from '../../../unit';
import { IkeAngle } from '../../../unit/angle';
import { IkeXML } from '../../../xml';
import { IkeDraw } from '../../draw';
import { IkeDrawState } from '../../draw.state';
import { LabelSVGDraw } from '../../drawable/label.draw.svg';
import { LineSVGDraw } from '../../drawable/line.draw.svg';
import { MarkerSVGDraw } from '../../drawable/marker.draw.svg';
import { MaskSVGDraw } from '../../drawable/mask.draw.svg';
import { IkeDrawLabel } from '../../drawable/util.draw.svg';
import { VerticalSeparationSVGDraw } from '../../drawable/vertical.separation.draw.svg';
import { m } from '../../mithril';
import { ClassNames } from '../../theme';
import { Crosshair } from '../crosshair';
import { RollIndicator } from '../rollIndicator';
import { rose } from '../rose';
import { STANDALONE_SVG_CSS } from './svg.export.style';
import { SVGStyles } from './svg.style';
import { Timezones } from './timezones';

export interface SVGContext {
	/** whether to add input event listeners (default: true) */
	interactive?: boolean; //
	/* whether to display the image as a thumbnail (default: false), */
	thumbnail?: boolean;
}

export const CROSSHAIR_CLASSES = {
	innerLine: 'c-Draw-crosshair--innerline',
	outerLine: 'c-Draw-crosshair--outerline',
	distance: 'c-Draw-crosshair--distance',
};

const EXPORT_STRING_OPTIONS = {
	keyMap: { className: 'class' },
	ignoreKeys: ['oncreate', 'onupdate', 'onremove'],
};

type DrawableKind = 'hovered' | 'selected' | 'drawable' | 'mask';

const DRAWABLE_PRIORITY: { [k in DrawableKind]: number } = {
	hovered: 0,
	selected: 1,
	drawable: 2,
	mask: 3,
} as const;

export interface SVGRenderOptions {
	draw: IkeDraw;
	units: IkeUnitDistance;
	isInsightMarkerLabelsVisible?: boolean;
	insightMarkerTypesVisible?: string[];
	isInsightMarkersVisible?: boolean | null;
	insightMaskTypesVisible?: string[] | null;
	isInsightMaskVisible?: boolean | null;
	isInteractive: boolean;
	/** Should this be rendered as a thumbnail (@see SVGRenderOptions.size) */
	isThumbnail: boolean;
	isEditable: boolean;

	/** Disable rendering of image */
	isImageDisabled?: boolean;

	isVerticalSeparationEnabled?: boolean;
	/* Set automatically if not set */
	scale: number;

	/** Only needed if isThumbnail is set */
	size?: Size;
	timezone?: string;

	// Use Spans colors
	useColorCoding?: boolean;

	// Show span orientation indicator
	showOrientation?: boolean | null;
	orientationBearing?: number | null;
	indicatorColor?: string | null;

	// Enable roll indicator
	enableRoll?: boolean;
}
export class SVGRenderer {
	static Style = SVGStyles;
	static CSS = ClassNames;

	static toSVGString(draw: IkeDraw, units: IkeUnitDistance, timezone: string, thumbnail = false): string {
		const ctx: SVGRenderOptions = {
			draw,
			units,
			timezone,
			isThumbnail: thumbnail,
			isInteractive: false,
			isEditable: false,
			isImageDisabled: true,
			scale: SVGRenderer.scaleUI(draw.state, thumbnail),
			isVerticalSeparationEnabled: true,
		};

		const vNodes = SVGRenderer.view(ctx);
		return IkeXML.Export.asString(vNodes, EXPORT_STRING_OPTIONS);
	}

	/**
	 * Render a standalone `<svg>` document string (no surrounding `<div>` wrapper) with the
	 * canonical c-Draw CSS embedded as a `<style>` block so the document is self-contained
	 * for external consumers that overlay the annotations on their own copy of
	 * the capture.
	 */
	static toSVGDocumentString(draw: IkeDraw, units: IkeUnitDistance, timezone: string, thumbnail = false): string {
		const ctx: SVGRenderOptions = {
			draw,
			units,
			timezone,
			isThumbnail: thumbnail,
			isInteractive: false,
			isEditable: false,
			isImageDisabled: true,
			scale: SVGRenderer.scaleUI(draw.state, thumbnail),
			isVerticalSeparationEnabled: true,
		};

		const svgNode = SVGRenderer.viewSVG(ctx);
		// Declare the SVG namespace so the document is recognised as SVG when opened directly
		// as a file (browsers fall back to generic XML parsing without this).
		svgNode.attrs.xmlns = 'http://www.w3.org/2000/svg';
		const styleNode = m('style', { type: 'text/css' }, STANDALONE_SVG_CSS);
		svgNode.children = [styleNode, ...svgNode.children];
		return IkeXML.Export.asString(svgNode, EXPORT_STRING_OPTIONS);
	}

	static scaleUI(drawState: IkeDrawState, isThumbnail: boolean): number {
		if (isThumbnail) {
			return drawState.scale;
		}
		return drawState.scale / drawState.zoom;
	}

	static viewCrosshair(ctx: SVGRenderOptions) {
		if (!ctx.draw.state.isCrosshairVisible || ctx.draw.capture.distance == null) {
			return null;
		}
		const resolution = ctx.draw.capture.resolution;
		const center = { x: resolution.width / 2, y: resolution.height / 2 };
		const gap = 15;
		const size = { width: 150, height: 150 };
		return Crosshair.view(ctx.draw, {
			center,
			size,
			gap,
			units: ctx.units,
			classNames: CROSSHAIR_CLASSES,
			scale: ctx.scale,
			outerStroke: 'rgba(0,0,0,0.5)',
			innerStroke: 'red',
			distance: ctx.draw.geometry3d.getDistance(),
		});
	}

	static viewRoll(ctx: SVGRenderOptions) {
		const roll = ctx.draw.capture.raw?.orientation?.roll ?? null;

		if (!ctx.enableRoll || !ctx.draw.state.isRollVisible || roll === null) {
			return null;
		}

		return RollIndicator.view(ctx.draw, {
			roll,
			width: ctx.draw.capture.resolution.width,
			height: ctx.draw.capture.resolution.height,
			zoomLevel: ctx.draw.state.zoom,
			scale: ctx.scale,
			resHeight: ctx.draw.capture.resolution.height,
		});
	}

	static viewSVG(ctx: SVGRenderOptions) {
		ctx.scale = ctx.scale || this.scaleUI(ctx.draw.state, ctx.isThumbnail);

		const drawState = ctx.draw.drawState;
		const toDraw = drawState.values();
		const data = [];
		const capture = ctx.draw.capture;
		const zoomLevel = ctx.draw.state.zoom;
		const centerPoint = ctx.draw.state.center;
		const viewBox = getViewBox(capture.resolution, centerPoint, zoomLevel);

		data.push(SVGRenderer.viewCrosshair(ctx));
		data.push(SVGRenderer.viewRoll(ctx));

		let nodes = toDraw
			// Identify what type of drawable we're creating
			.map((drawable) => {
				const id = drawable.id;
				let kind: DrawableKind = CollectionModelDrawableMask.isMask(drawable) ? 'mask' : 'drawable';

				if (ctx.isInteractive) {
					if (id === ctx.draw.state.selectedId) {
						kind = 'selected';
					} else if (id === ctx.draw.state.hoverId) {
						kind = 'hovered';
					}
				}

				return {
					kind,
					drawable,
				};
			})
			// Sort the drawables so that the the hovered and selected drawables are rendered on top
			.sort((a, b) => DRAWABLE_PRIORITY[b.kind] - DRAWABLE_PRIORITY[a.kind])
			// Get the mithril nodes
			.map(({ drawable, kind }) => {
				return SVGRenderer.renderDrawable(ctx, drawable);
			})
			.filter(Boolean);

		let compassDrawable = drawState.baseOffset || drawState.base || null;

		nodes = [
			...nodes,
			this.renderOrientationIndicator(ctx, compassDrawable, ctx.showOrientation || false),
			this.renderDateLabel(ctx, viewBox[3], zoomLevel),
			this.renderBearingLabel(ctx, viewBox[2], viewBox[3], zoomLevel),
		];

		// if it's measurable (trusize image) we should not put this layer on the top of the markers
		// see bug: https://surveylab.atlassian.net/browse/IKE4-3258
		if (!ctx.isThumbnail && !ctx.draw.isIkePhoto) {
			let [x, y, width, height] = [
				ctx.draw.state.cropX,
				ctx.draw.state.cropY,
				ctx.draw.state.cropWidth,
				ctx.draw.state.cropHeight,
			];

			//if there is no cropping region set, then all the capture is the cropping region
			if (ctx.draw.state.cropWidth === 0 || ctx.draw.state.cropHeight === 0) {
				[x, y, width, height] = [0, 0, capture.resolution.width, capture.resolution.height];
			}

			const cropView = m('rect', {
				className: ClassNames.cropWindow,
				'fill-opacity': 0.0,
				width,
				height,
				x,
				y,
			});
			if (zoomLevel > 1) {
				cropView.attrs['c-Draw-draggable'] = 'true';
				cropView.attrs.className += ' ' + ClassNames.isMoveable;
				cropView.attrs['c-Draw-draggable'] = 'true';
				cropView.attrs['c-Draw-node-id'] = 'image';
				cropView.attrs['c-Draw-Label'] = IkeDrawLabel.IMAGE;
				cropView.attrs['c-Draw-tag'] = 'image';
			} else {
				cropView.attrs['c-Draw-draggable'] = 'false';
				cropView.attrs.className = ClassNames.cropWindow;
				cropView.attrs['c-Draw-draggable'] = 'false';
				cropView.attrs['c-Draw-node-id'] = 'rect';
				//cropView.attrs['c-Draw-Label'] = IkeDrawLabel.IMAGE;
				//cropView.attrs['c-Draw-tag'] = 'rect';
			}
			nodes.push(cropView);
		}

		ctx.draw.log.debug(
			{
				collection: ctx.draw.collection.id,
				capture: ctx.draw.capture.objectId,
				nodes: [...nodes],
			},
			'SVGRenderer: rendering drawable nodes for capture'
		);

		const drawableContainer = m('g', { className: 'c-Drawables' }, nodes);

		data.push(drawableContainer);

		data.push(SVGRenderer.renderImage(ctx, capture, zoomLevel));

		data.push(SVGRenderer.renderFilters(ctx));

		const rootSVG = m(
			'svg',
			{
				className: ClassNames.svg,
				style: SVGStyles[ClassNames.svg],
				'data-capture-id': drawState.captureId,
			},
			data.reverse()
		);

		if (ctx.isInteractive) {
			ctx.draw.boundEvents.lastRenderOptions = ctx;
			rootSVG.attrs.onclick = ctx.draw.boundEvents.click;
			rootSVG.attrs.ondblclick = ctx.draw.boundEvents.doubleclick;
			rootSVG.attrs.onmousedown = ctx.draw.boundEvents.mousedown;
			rootSVG.attrs.onmousemove = ctx.draw.boundEvents.mousemove;
			rootSVG.attrs.onwheel = ctx.draw.boundWheelEvent;

			// No requirement for touch support.
			//rootSVG.attrs.ontouchmove = SVGEventHandler.mousemove.bind(null, this.dc);
			//rootSVG.attrs.ontouchstart = SVGEventHandler.mousedown.bind(null, this.dc);
			rootSVG.attrs.className += ' ' + ClassNames.isInteractive;
		} else {
			rootSVG.attrs.className += ' ' + ClassNames.isStatic;
		}

		if (ctx.draw.isMoving) {
			rootSVG.attrs.className += ' ' + ClassNames.isMoving;
		}

		if (ctx.draw.isLinking) {
			rootSVG.attrs.className += ' ' + ClassNames.isLinking;
		}

		const currentTool = ctx.draw.state.tool;
		if (ctx.isInteractive && currentTool != null) {
			rootSVG.attrs.className += ` ${ClassNames.isToolActive} c-Draw-Tool-${currentTool}`;
		}

		if (ctx.isThumbnail) {
			rootSVG.attrs.width = ctx.size.width;
			rootSVG.attrs.height = ctx.size.height;
			rootSVG.attrs.className += ' ' + ClassNames.isThumbnail;
			rootSVG.attrs.preserveAspectRatio = 'xMinYMin slice';
			rootSVG.attrs.viewBox = getThumbnailViewBox(capture.resolution, ctx.size).join(' ');
			if (zoomLevel > 1) {
				const [x, y, width, height] = viewBox;
				const currentView = m('rect', {
					className: ClassNames.thumbnailZoomWindow,
					fill: 'rgba(0,0,0,0.35)',
					width,
					height,
					x,
					y,
				});
				rootSVG.children.push(currentView);
			}
		} else {
			rootSVG.attrs.viewBox = viewBox.join(' ');
			rootSVG.attrs.preserveAspectRatio = 'xMinYMin';
		}

		return rootSVG;
	}

	static view(ctx: SVGRenderOptions) {
		const rootSVG = SVGRenderer.viewSVG(ctx);

		let rootClassName = ClassNames.container;
		if (ctx.isThumbnail) {
			rootClassName += ' ' + ClassNames.isThumbnail;
		}

		return m(
			'div',
			{
				className: rootClassName,
				style:
					ctx.draw.orientation === 'landscape'
						? {
								display: 'flex',
								'flex-direction': 'column',
								'justify-content': 'center',
								'align-items': 'center',
							}
						: {},
			},
			rootSVG
		);
	}
	static filterId(dc: IkeDraw) {
		return `${dc.id}:brightness`;
	}
	static renderFilters(ctx: SVGRenderOptions) {
		// https://github.com/iamvdo/pleeease-filters

		const feFuncAttrs = { type: 'gamma', exponent: ctx.draw.state.brightness };
		return m('defs', null, [
			m('filter', { id: SVGRenderer.filterId(ctx.draw) }, [
				m('feComponentTransfer', { 'color-interpolation-filters': 'sRGB' }, [
					m('feFuncR', feFuncAttrs),
					m('feFuncG', feFuncAttrs),
					m('feFuncB', feFuncAttrs),
				]),
			]),
		]);
	}

	static renderDateLabel(ctx: SVGRenderOptions, height: number, zoomLevel: number) {
		if (!ctx.draw.state.isDateLabelVisible) {
			return null;
		}
		const rectHeight = ctx.scale * LabelSVGDraw.height(1);
		const yPos = height * zoomLevel - rectHeight;
		const xOffset = (LabelSVGDraw.Size.width * ctx.scale) / 2;
		const node = m(
			'g',
			{
				transform: `translate(0, ${yPos})`,
			},
			[
				m('rect', {
					x: '0',
					y: '0',
					width: ctx.scale * LabelSVGDraw.Size.width,
					height: rectHeight,
					fill: 'rgb(224,224,224)',
					'fill-opacity': 0.75,
					stoke: 'none',
					'stroke-width': 0,
				}),
				m('g', { transform: `translate(${xOffset}, 0) scale(${ctx.scale})` }, [
					m(
						'text',
						{
							style: 'font-size: 14px; user-select: none; pointer-events: none;',
							x: '0',
							y: '1em',
							width: ctx.scale * LabelSVGDraw.Size.width,
							'text-anchor': 'middle',
							dy: '0.25em',
						},
						formatDateTime(ctx.draw.capture.collectedAt.iso, ctx.timezone)
					),
				]),
			]
		);
		return node;
	}

	static renderBearingLabel(ctx: SVGRenderOptions, width: number, height: number, zoomLevel: number) {
		if (!ctx.draw.state.isBearingLabelVisible || ctx.draw.capture.orientation == null) {
			return null;
		}

		const rectHeight = ctx.scale * LabelSVGDraw.height(1);
		const rectWidth = ctx.scale * LabelSVGDraw.Size.width;
		const xOffset = rectWidth / 2;

		const yPos = height * zoomLevel - rectHeight;
		const xPos = (width * zoomLevel - rectWidth) / 2;

		const node = m(
			'g',
			{
				transform: `translate(${xPos}, ${yPos})`,
			},
			[
				m('rect', {
					x: '0',
					y: '0',
					width: ctx.scale * LabelSVGDraw.Size.width,
					height: rectHeight,
					fill: 'rgb(224,224,224)',
					'fill-opacity': 0.75,
					stoke: 'none',
					'stroke-width': 0,
				}),
				m('g', { transform: `translate(${xOffset}, 0)scale(${ctx.scale})` }, [
					m(
						'text',
						{
							style: 'font-size: 14px; user-select: none; pointer-events: none;',
							x: '0',
							y: '1em',
							width: ctx.scale * LabelSVGDraw.Size.width,
							'text-anchor': 'middle',
							dy: '0.25em',
						},
						IkeUnit.displayAngle(ctx.draw.capture.orientation.bearing, IkeUnitAngle.DEGREE, IkeUnitAngle.RADIAN, 0)
					),
				]),
			]
		);
		return node;
	}

	static renderImage(ctx: SVGRenderOptions, image: CaptureFileJSONObject, zoomFactor: number) {
		if (ctx.isImageDisabled) {
			return null;
		}

		const imageUrl = !ctx.isEditable && image.medium ? image.medium.url : image.file.url;

		if (!ctx.isThumbnail && !ctx.draw.isImageReady(image.objectId)) {
			ctx.draw.loadImage(image.objectId, imageUrl);
			return null;
		}

		let href: string = imageUrl;
		const width: number = image.resolution.width;
		const height: number = image.resolution.height;
		if (ctx.isThumbnail && image.thumbnail) {
			href = image.thumbnail.url;
		}
		const imageNode = m('image', {
			className: ClassNames.image,
			width,
			height,
			x: 0,
			y: 0,
			'xlink:href': href,
			// Stop firefox from grabbing the image
			draggable: false,
			ondragstart: (e) => e.preventDefault(),
		});

		if (zoomFactor > 1) {
			imageNode.attrs.className += ' ' + ClassNames.isMoveable;
			imageNode.attrs['c-Draw-draggable'] = 'true';
			imageNode.attrs['c-Draw-node-id'] = 'image';
			imageNode.attrs['c-Draw-Label'] = IkeDrawLabel.IMAGE;
			imageNode.attrs['c-Draw-tag'] = 'image';
		}

		if (ctx.draw.state.brightness !== 1) {
			imageNode.attrs['filter'] = `url(#${SVGRenderer.filterId(ctx.draw)})`;
		}

		imageNode.attrs.className += ' ' + ClassNames.isImageLoaded;
		return imageNode;
	}

	static renderOrientationIndicator(
		ctx: SVGRenderOptions,
		obj: CollectionModelDrawableMarker | CollectionModelDrawableMarkerOffset,
		withOrientationIndicator: boolean
	) {
		if (!withOrientationIndicator || !obj) {
			return null;
		}

		let opts = {
			y: obj.y,
			captureBearing: get(obj, ['drawableState', 'capture', 'orientation', 'bearing'], null),
			orientationBearing: ctx.orientationBearing,
			color: ctx.indicatorColor,
		};

		if (typeof opts.orientationBearing === 'number') {
			const northRotation = 360 - IkeAngle.toDegrees(opts.captureBearing);
			const indicatorRotation = northRotation + IkeAngle.toDegrees(opts.orientationBearing);

			return m(
				'svg',
				{
					class: 'c-Draw-orientation-indicator',
					y: `${opts.y}px`,
					x: '50%',
					style: {
						overflow: 'visible',
						'z-index': -10,
						color: opts.color,
					},
				},
				m(
					'g',
					{
						style: {
							transform: 'translate3d(-200px, -65px, 1px)',
						},
					},
					m(
						'g',
						{
							style: {
								transform: `perspective(300px) rotateX(70deg)`,
							},
						},
						m(
							'g',
							{
								transform: `rotate(${indicatorRotation}, 200, 200)`,
							},
							rose
						)
					)
				)
			);
		}

		return null;
	}

	static renderDrawable(ctx: SVGRenderOptions, obj: CollectionModelDrawable) {
		let node;

		if (CollectionModelDrawableMarker.isMarker(obj)) {
			node = MarkerSVGDraw.view(obj, ctx);
		} else if (CollectionModelDrawableMarkerOffset.isBaseOffset(obj)) {
			node = MarkerSVGDraw.view(obj, ctx);
		} else if (CollectionModelDrawableLabel.isLabel(obj) && obj.parent == null) {
			node = LabelSVGDraw.view(obj, ctx);
		} else if (CollectionModelDrawableLine.isLine(obj)) {
			node = LineSVGDraw.view(obj, ctx);
		} else if (CollectionModelDrawableMask.isMask(obj)) {
			const maskFetcher = ctx.draw.captureMaskFetcher;
			if (maskFetcher) {
				obj
					.fetchSvgMask(async () => await maskFetcher(ctx.draw.collection.id, ctx.draw.capture.objectId))
					.catch((error) => {
						// We have to catch and log the error here or the app might crash
						// due to an uncaught promise rejection.

						ctx.draw.log.error({ error }, 'SVGRenderer: caught error while fetching mask');
					});
			}
			node = MaskSVGDraw.view(obj, ctx);
		} else if (obj instanceof CollectionModelDrawableVerticalSeparation) {
			node = VerticalSeparationSVGDraw.view(obj, ctx);
		}

		if (node) {
			node.key = obj.id;
		}

		return node;
	}
}

function getThumbnailViewBox(resolution: Size, thumbnailSize: Size) {
	const dimensions = scaleCenterCrop(resolution, thumbnailSize);
	const viewX = (dimensions.left * 1) / dimensions.scale;
	const viewY = (dimensions.top * 1) / dimensions.scale;
	return [-viewX, -viewY, resolution.width + viewX, resolution.height + viewY];
}

function getViewBox(resolution: Size, center: Point, zoomFactor: number) {
	const zoomWidth = resolution.width / zoomFactor / 2;
	const zoomHeight = resolution.height / zoomFactor / 2;

	if (center.x - zoomWidth < 0) {
		center.x = zoomWidth;
	} else if (center.x + zoomWidth > resolution.width) {
		center.x = resolution.width - zoomWidth;
	}

	if (center.y - zoomHeight < 0) {
		center.y = zoomHeight;
	} else if (center.y + zoomHeight > resolution.height) {
		center.y = resolution.height - zoomHeight;
	}

	return [center.x - zoomWidth, center.y - zoomHeight, zoomWidth * 2, zoomHeight * 2];
}

function scaleCenterCrop(source: Size, dest: Size) {
	// Compute the scaling factors to fit the new height and width, respectively.
	// To cover the final image, the final scaling will be the bigger
	// of these two.
	const xScale = dest.width / source.width;
	const yScale = dest.height / source.height;
	const scale = Math.max(xScale, yScale);

	// Now get the size of the source.bitmap when scaled
	const scaledWidth = scale * source.width;
	const scaledHeight = scale * source.height;

	// Let's find out the upper left coordinates if the scaled bitmap
	// should be centered in the new size give by the parameters
	const left = (dest.width - scaledWidth) / 2;
	const top = (dest.height - scaledHeight) / 2;

	return {
		top,
		left,
		width: scaledWidth > dest.width ? dest.width : scaledWidth,
		height: scaledHeight > dest.height ? dest.height : scaledHeight,
		scale,
	};
}

function formatDateTime(isoDate: string, timezone: string): string {
	const locale = 'en-GB';
	const searchString = isoDate.slice(0, -5) + '.000Z';
	const date = new Date(searchString);
	const timeZone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
	const datePart = `${date.toLocaleString(locale, { month: 'short', day: '2-digit', timeZone })}, ${date.toLocaleString(locale, { year: 'numeric', timeZone })} `;
	const output =
		datePart + ' ' + date.toLocaleString(locale, { hour: '2-digit', minute: '2-digit', hour12: true, timeZone });

	if (timeZone === 'UTC') {
		return output + ' (UTC)';
	}

	const tzshort = getTimezoneShort(timeZone);

	return `${output} (${tzshort})`;
}

function getTimezoneShort(tz: string): string {
	const result = Timezones.getTimezones().find(({ utc }) => utc.indexOf(tz) > 0);

	if (result === undefined) {
		return 'N/A';
	}

	return result.abbr;
}
